import RTable from '@ayocore/platform/client/components/RTable/RTable';
import type { RTableProps } from '@ayocore/platform/client/components/RTable/RTable.props';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { LoadingOverlay } from '@mantine/core';
import { useRepositorySelector } from './useRepositoryController';
import type { RepositoryController } from './RepositoryController';

export interface RepositoryTableWrapperProps<T extends Record<string, unknown>>
  extends Omit<RTableProps<T>, 'data' | 'pagination'> {
  controller: RepositoryController<T>;
  /** Показывать нижний блок: пагинацию и счётчик «Строк: n» / агрегаты (default: true). */
  showBottom?: boolean;
}

export interface RepositoryTableWrapperHandle<T extends Record<string, unknown>> {
  /**
   * Возвращает выбранные строки по состоянию rowSelection.
   * Ключи — бизнес-id (row.id), полученные из getRowId (см. ниже).
   * Если выбранная запись отсутствует на текущей странице — в результат не попадёт.
   */
  resolveSelectedRows: (rowSelection: Record<string, boolean>) => T[];
}

/**
 * Ключ строки для tanstack-table и rowSelection.
 * Используем row.id (бизнес-идентификатор), чтобы галочки сохранялись за записью,
 * а не за позицией в списке при смене страницы/фильтра/сортировки.
 */
function rowKey<T extends Record<string, unknown>>(row: T, relativeIndex: number): string {
  const id = row.id;
  return id == null ? String(relativeIndex) : String(id);
}

function RepositoryTableWrapperInner<T extends Record<string, unknown>>(
  props: RepositoryTableWrapperProps<T>,
  ref: React.Ref<RepositoryTableWrapperHandle<T>>
) {
  const {
    controller,
    id,
    columns,
    className,
    getRowId: externalGetRowId,
    showBottom = true,
    ...rest
  } = props;

  const rows = useRepositorySelector(controller, s => s.data) as T[];
  const total = useRepositorySelector(controller, s => s.totalCount);
  const isLoading = useRepositorySelector(controller, s => s.isLoading);
  const { page, pageSize } = controller.getQuery();

  const getRowId = useCallback((row: T, relativeIndex: number) => rowKey(row, relativeIndex), []);
  // Единый резолвер ключа: кастомный getRowId (если передан) и resolveSelectedRows
  // обязаны совпадать, иначе выбранные строки не разрешатся из rowSelection
  const resolveKey = externalGetRowId ?? getRowId;

  useImperativeHandle(
    ref,
    () => ({
      resolveSelectedRows: (rowSelection: Record<string, boolean>) =>
        rows.filter((row, idx) => Boolean(rowSelection[resolveKey(row, idx)])),
    }),
    [rows, resolveKey]
  );

  return (
    <div className={className} style={{ position: 'relative' }}>
      <LoadingOverlay
        visible={isLoading}
        zIndex={50}
        overlayProps={{ radius: 'sm', blur: 1, backgroundOpacity: 0.35 }}
        loaderProps={{ size: 'sm' }}
      />
      <RTable<T>
        id={id}
        data={rows}
        columns={columns}
        getRowId={resolveKey}
        pagination={{
          current: page,
          total,
          pageSize,
          showTop: false,
          showBottom,
          pageSizeOptions: [25, 50, 100, 500],
          onPageSizeChange: size => controller.setPageSize(size),
        }}
        {...rest}
      />
    </div>
  );
}

export const RepositoryTableWrapper = forwardRef(RepositoryTableWrapperInner) as <
  T extends Record<string, unknown>,
>(
  p: RepositoryTableWrapperProps<T> & { ref?: React.Ref<RepositoryTableWrapperHandle<T>> }
) => ReturnType<typeof RepositoryTableWrapperInner>;

export default RepositoryTableWrapper;
